import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Typography, Table, Button, Tag, Switch, Space, Modal, message, Breadcrumb, Tooltip, Avatar } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { WHITE } from "../../constant/colors";
import {
  getBrandJourneyConfigurationsAPI,
  updateBrandJourneyConfigurationAPI,
  deleteBrandJourneyConfigurationAPI
} from "../../api/api";
import { JourneyConfigurationForm } from "./JourneyConfigurationForm";

const { Title, Text } = Typography;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

const ContentSection = styled.div`
  flex: 1;
  overflow: auto;
`;

const JOURNEY_STEP_LABELS = {
  prescription: "Prescription",
  coverage: "Coverage",
  assistance: "Assistance",
  support: "Support"
};

const JOURNEY_STEP_COLORS = {
  prescription: "blue",
  coverage: "green",
  assistance: "orange",
  support: "purple"
};

const JourneyConfiguration = () => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const fetchConfigurations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getBrandJourneyConfigurationsAPI();
      setConfigurations(data);
    } catch (error) {
      message.error("Failed to load brand configurations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const handleToggleActive = async (record) => {
    try {
      await updateBrandJourneyConfigurationAPI(record.id, { isActive: !record.isActive });
      message.success(`Brand "${record.brandName}" ${record.isActive ? "deactivated" : "activated"}`);
      fetchConfigurations();
    } catch (error) {
      message.error("Failed to update brand status");
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete "${record.brandName}"?`,
      icon: <ExclamationCircleOutlined />,
      content: "This will permanently remove this brand configuration and all associated settings.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteBrandJourneyConfigurationAPI(record.id);
          message.success(`Brand "${record.brandName}" deleted`);
          fetchConfigurations();
        } catch (error) {
          message.error("Failed to delete brand configuration");
        }
      }
    });
  };

  const handleEdit = (record) => {
    setEditingConfig(record);
    setFormVisible(true);
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setFormVisible(true);
  };

  const handleFormClose = (saved) => {
    setFormVisible(false);
    setEditingConfig(null);
    if (saved) fetchConfigurations();
  };

  const getEnabledSteps = (journeySteps) => {
    if (!journeySteps) return [];
    return Object.entries(journeySteps)
      .filter(([, step]) => step.enabled)
      .map(([key]) => key);
  };

  const columns = [
    {
      title: "Brand Name",
      dataIndex: "brandName",
      key: "brandName",
      width: 200,
      render: (text, record) => (
        <Space>
          {record.brandLogoUrl && <Avatar src={record.brandLogoUrl} shape="square" size={32} />}
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: "Journey Steps",
      dataIndex: "journeySteps",
      key: "journeySteps",
      render: (journeySteps) => {
        const enabledSteps = getEnabledSteps(journeySteps);
        return (
          <Space wrap>
            {enabledSteps.map((step) => (
              <Tag key={step} color={JOURNEY_STEP_COLORS[step]}>
                {JOURNEY_STEP_LABELS[step]}
              </Tag>
            ))}
            {enabledSteps.length === 0 && <Text type="secondary">No steps enabled</Text>}
          </Space>
        );
      }
    },
    {
      title: "Hotline",
      dataIndex: "manufacturerHotlineNumber",
      key: "hotline",
      width: 150,
      render: (text) => text || <Text type="secondary">Not set</Text>
    },
    {
      title: "Drug",
      dataIndex: "rxnormDrugName",
      key: "drug",
      width: 150,
      render: (name) => (name ? <Text style={{ fontSize: 12 }}>{name}</Text> : <Text type="secondary">-</Text>)
    },
    {
      title: "Invite Link",
      dataIndex: "inviteSlug",
      key: "inviteLink",
      width: 180,
      render: (slug) =>
        slug ? (
          <Typography.Text
            copyable={{ text: `${window.location.origin}/consumer/invite/${slug}` }}
            style={{ fontSize: 11 }}
          >
            /invite/{slug}
          </Typography.Text>
        ) : (
          <Text type="secondary">-</Text>
        )
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (isActive, record) => (
        <Switch checked={isActive} onChange={() => handleToggleActive(record)} size="small" />
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <PageContainer>
      <HeaderSection>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/settings">Settings</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Journey Configuration</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Journey Configuration
            </Title>
            <Text type="secondary">
              Configure brand-specific patient journeys. Each brand defines what steps and content patients see in TM
              Connect.
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add Brand
          </Button>
        </div>
      </HeaderSection>

      <ContentSection>
        <Table
          columns={columns}
          dataSource={configurations}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: "No brand configurations yet. Click 'Add Brand' to get started." }}
        />
      </ContentSection>

      {formVisible && (
        <JourneyConfigurationForm visible={formVisible} config={editingConfig} onClose={handleFormClose} />
      )}
    </PageContainer>
  );
};

export { JourneyConfiguration };
